import { useEffect, useState } from 'react';

interface UseStepsOptions<T extends string> {
  /** Ordered list of step identifiers. First step is the default. */
  steps: readonly T[];
  /** Base URL used to build breadcrumb hrefs (without step param). */
  baseUrl: string;
  /** URL search param name used for step navigation. Defaults to 'step'. */
  paramName?: string;
  /** Callback fired when the step changes via breadcrumb (URL param). */
  onStepChange?: (step: T) => void;
}

interface UseStepsReturn<T extends string> {
  /** The current active step. */
  currentStep: T;
  /** Update the current step. */
  setCurrentStep: (step: T) => void;
  /** Build a breadcrumb-compatible URL for a given step. */
  getStepUrl: (step: T) => string;
}

export const useSteps = <T extends string>({
  steps,
  baseUrl,
  paramName = 'step',
  onStepChange,
}: UseStepsOptions<T>): UseStepsReturn<T> => {
  const defaultStep = steps[0];

  const getStepFromUrl = (): T | null => {
    const params = new URLSearchParams(window.location.search);
    const value = params.get(paramName);
    if (value && (steps as readonly string[]).includes(value)) {
      return value as T;
    }
    return null;
  };

  const [currentStep, setCurrentStep] = useState<T>(defaultStep);

  useEffect(() => {
    const stepFromUrl = getStepFromUrl();
    if (stepFromUrl) {
      setCurrentStep(stepFromUrl);
      onStepChange?.(stepFromUrl);

      // Clean the step param from the URL
      const params = new URLSearchParams(window.location.search);
      params.delete(paramName);
      const search = params.toString();
      const cleanUrl = `${window.location.pathname}${
        search ? `?${search}` : ''
      }`;
      window.history.replaceState(null, '', cleanUrl);
    }
  }, [window.location.search]);

  const getStepUrl = (step: T): string => {
    const [pathname, search] = baseUrl.split('?');
    const params = new URLSearchParams(search || '');
    params.set(paramName, step);
    return `${pathname}?${params.toString()}`;
  };

  return { currentStep, setCurrentStep, getStepUrl };
};
